import React, { useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  ReferenceLine,
} from "recharts";
import { getDetailedClimateColor } from "../utils/climateClassification";
import koppenDescriptions from "../data/koppen-descriptions.json";
import {
  OPENROUTER_API_KEY,
  API_BASE_URL,
  isConfigValid,
} from "../utils/config";

const HumidityAnalysis = ({ climateInfo, humidityData }) => {
  const [aiInterpretation, setAiInterpretation] = useState(null);
  const [loadingAiInterpretation, setLoadingAiInterpretation] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [designSuggestions, setDesignSuggestions] = useState(null);
  const [loadingDesignSuggestions, setLoadingDesignSuggestions] =
    useState(false);
  const [designError, setDesignError] = useState(null);

  if (!climateInfo || !humidityData) {
    return null;
  }

  // Calculate humidity performance metrics
  const annualAvgHumidity = humidityData.data
    ? humidityData.data.reduce((sum, d) => sum + d.mean, 0) /
      humidityData.data.length
    : 0;

  const maxHumidity = humidityData.data
    ? Math.max(...humidityData.data.map((d) => d.max || d.mean))
    : 0;

  const minHumidity = humidityData.data
    ? Math.min(...humidityData.data.map((d) => d.min || d.mean))
    : 0;

  const humidityRange = maxHumidity - minHumidity;

  // Calculate comfort days (40-60% humidity is generally considered comfortable)
  const comfortableDays = humidityData.data
    ? humidityData.data.filter((d) => d.mean >= 40 && d.mean <= 60).length
    : 0;

  const comfortablePercentage = humidityData.data
    ? (comfortableDays / humidityData.data.length) * 100
    : 0;

  return (
    <div
      id="humidity-analysis-section"
      className="min-h-screen bg-gray-100 px-14 py-8 pt-28"
    >
      {/* Humidity Heading */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 tracking-wide">
          Humidity
        </h1>
      </div>

      {/* Three Cards Layout - Top Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* MACRO Card */}
        <div className="bg-gray-100 border border-gray-500 p-8 rounded-[2rem]">
          <div className="border-b border-gray-300 pb-2 mb-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              MACRO
            </h3>
          </div>

          {/* Top section - humidity metrics */}
          <div className="flex justify-between items-start mb-8">
            {/* Left side - Max and Min humidity */}
            <div className="flex-1">
              <div className="mb-4">
                <div className="text-sm text-gray-500 font-bold mb-1">
                  Maximum Humidity
                </div>
                <div className="text-2xl font-black text-gray-900">
                  {maxHumidity.toFixed(1)}%
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500 font-bold mb-1">
                  Minimum Humidity
                </div>
                <div className="text-2xl font-black text-gray-900">
                  {minHumidity.toFixed(1)}%
                </div>
              </div>
            </div>

            {/* Right side - Annual average humidity */}
            <div className="flex-1 text-right">
              <div className="text-5xl font-black text-black mb-2">
                {annualAvgHumidity.toFixed(1)}%
              </div>
              <div className="text-sm text-gray-500 font-bold">
                Annual Avg. Humidity
              </div>
            </div>
          </div>

          {/* Bottom section - Classification information */}
          <div>
            {/* Large Köppen classification code */}
            <div
              className="text-6xl font-black leading-none mb-4"
              style={{
                color: getDetailedClimateColor(
                  climateInfo.mainClass,
                  climateInfo.subClass
                ),
              }}
            >
              {climateInfo.mainClass}
              {climateInfo.subClass}
            </div>

            {/* Classification label */}
            <div className="text-sm text-gray-500 font-bold mb-4">
              Köppen-Geiger Climate Classification
            </div>

            {/* Climate name */}
            <div className="font-bold text-gray-800 mb-4 text-lg">
              {climateInfo.fullName}
            </div>

            {/* Description */}
            <div className="text-sm text-gray-500 leading-relaxed font-bold">
              <div>
                {annualAvgHumidity >= 80
                  ? "Very humid conditions year-round with potential for moisture-related issues."
                  : annualAvgHumidity >= 60
                  ? "Humid climate with moderate to high moisture levels throughout the year."
                  : annualAvgHumidity >= 40
                  ? "Moderate humidity levels that are generally comfortable for most activities."
                  : "Dry atmospheric conditions with low humidity levels that may require moisture management."}
              </div>
            </div>
          </div>
        </div>

        {/* MICRO Card */}
        <div className="bg-gray-100 border border-gray-500 p-8 rounded-[2rem] flex flex-col h-full">
          <div className="border-b border-gray-300 pb-2 mb-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              MICRO
            </h3>
          </div>

          {/* Humidity Range at top */}
          <div className="text-left mb-8">
            <div className="text-5xl font-black text-black mb-2">
              {humidityRange.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-500 font-bold">
              Humidity Range
            </div>
          </div>

          {/* Seasonal Humidity Patterns */}
          <div className="mb-8 flex-grow">
            <h4 className="font-bold text-gray-500 mb-4 text-lg">
              Seasonal Patterns
            </h4>

            <div className="grid grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4 text-sm">
                <div>
                  <div className="font-bold text-gray-800 mb-1">Winter:</div>
                  <div className="font-bold text-gray-500">
                    Avg:{" "}
                    {humidityData.data
                      ? (() => {
                          const winterData = humidityData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (humidityData.data.length / 12)
                                  ) + 1;
                              return month === 12 || month === 1 || month === 2;
                            }
                          );
                          return winterData.length > 0
                            ? (
                                winterData.reduce((sum, d) => sum + d.mean, 0) /
                                winterData.length
                              ).toFixed(1)
                            : "--";
                        })()
                      : "--"}
                    %
                  </div>
                </div>

                <div>
                  <div className="font-bold text-gray-800 mb-1">Spring:</div>
                  <div className="font-bold text-gray-500">
                    Avg:{" "}
                    {humidityData.data
                      ? (() => {
                          const springData = humidityData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (humidityData.data.length / 12)
                                  ) + 1;
                              return month === 3 || month === 4 || month === 5;
                            }
                          );
                          return springData.length > 0
                            ? (
                                springData.reduce((sum, d) => sum + d.mean, 0) /
                                springData.length
                              ).toFixed(1)
                            : "--";
                        })()
                      : "--"}
                    %
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4 text-sm">
                <div>
                  <div className="font-bold text-gray-800 mb-1">Summer:</div>
                  <div className="font-bold text-gray-500">
                    Avg:{" "}
                    {humidityData.data
                      ? (() => {
                          const summerData = humidityData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (humidityData.data.length / 12)
                                  ) + 1;
                              return month === 6 || month === 7 || month === 8;
                            }
                          );
                          return summerData.length > 0
                            ? (
                                summerData.reduce((sum, d) => sum + d.mean, 0) /
                                summerData.length
                              ).toFixed(1)
                            : "--";
                        })()
                      : "--"}
                    %
                  </div>
                </div>

                <div>
                  <div className="font-bold text-gray-800 mb-1">Fall:</div>
                  <div className="font-bold text-gray-500">
                    Avg:{" "}
                    {humidityData.data
                      ? (() => {
                          const fallData = humidityData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (humidityData.data.length / 12)
                                  ) + 1;
                              return (
                                month === 9 || month === 10 || month === 11
                              );
                            }
                          );
                          return fallData.length > 0
                            ? (
                                fallData.reduce((sum, d) => sum + d.mean, 0) /
                                fallData.length
                              ).toFixed(1)
                            : "--";
                        })()
                      : "--"}
                    %
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Humidity Extremes */}
          <div className="flex justify-between items-center mt-auto">
            <div className="text-left">
              <div className="text-4xl font-black text-black">
                {minHumidity.toFixed(1)}%
              </div>
              <div className="text-sm text-gray-500 font-bold">
                minimum humidity
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-black text-black">
                {maxHumidity.toFixed(1)}%
              </div>
              <div className="text-sm text-gray-500 font-bold">
                maximum humidity
              </div>
            </div>
          </div>
        </div>

        {/* DESIGN Card */}
        <div className="bg-gray-100 border border-gray-500 p-8 rounded-[2rem] flex flex-col h-full">
          <div className="border-b border-gray-300 pb-2 mb-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              DESIGN
            </h3>
          </div>

          <div className="flex space-x-4 mb-4">
            <div className="w-20 h-14 bg-gray-800 rounded flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-white rounded-sm"></div>
            </div>
            <div className="w-20 h-14 bg-gray-800 rounded flex items-center justify-center">
              <div className="w-8 h-8 bg-white transform rotate-45"></div>
            </div>
          </div>

          <div className="mb-4 flex-grow">
            <h4 className="font-bold text-gray-800 mb-2">
              Climate Interpretation & Design Implications
            </h4>

            {/* Conditional content: Show loading, AI suggestions, error, or static text */}
            {loadingDesignSuggestions ? (
              <div className="text-sm text-gray-700 space-y-2">
                <style jsx>{`
                  @keyframes humidityWave1 {
                    0%,
                    100% {
                      background-color: #d1d5db;
                    }
                    50% {
                      background-color: #9ca3af;
                    }
                  }
                  @keyframes humidityWave2 {
                    0%,
                    100% {
                      background-color: #9ca3af;
                    }
                    50% {
                      background-color: #d1d5db;
                    }
                  }
                  @keyframes humidityWave3 {
                    0%,
                    100% {
                      background-color: #e5e7eb;
                    }
                    50% {
                      background-color: #9ca3af;
                    }
                  }
                  .animate-humidity-wave-1 {
                    animation: humidityWave1 2s ease-in-out infinite;
                  }
                  .animate-humidity-wave-2 {
                    animation: humidityWave2 2s ease-in-out infinite 0.3s;
                  }
                  .animate-humidity-wave-3 {
                    animation: humidityWave3 2s ease-in-out infinite 0.6s;
                  }
                `}</style>
                <div className="h-3 rounded animate-humidity-wave-1"></div>
                <div className="h-3 rounded animate-humidity-wave-2 w-4/5"></div>
                <div className="h-3 rounded animate-humidity-wave-3 w-3/5"></div>
                <div className="h-3 rounded animate-humidity-wave-1 w-5/6"></div>
                <div className="h-3 rounded animate-humidity-wave-2 w-2/3"></div>
                <div className="h-3 rounded animate-humidity-wave-3"></div>
              </div>
            ) : designSuggestions ? (
              <div className="text-sm text-gray-500 leading-relaxed font-bold whitespace-pre-wrap">
                {designSuggestions}
              </div>
            ) : designError ? (
              <div className="text-red-600 text-sm font-bold">
                Error: {designError}
              </div>
            ) : (
              <div className="text-sm text-gray-700 space-y-2">
                <p>
                  This location has{" "}
                  {annualAvgHumidity >= 80
                    ? "very high humidity requiring significant moisture control and ventilation strategies"
                    : annualAvgHumidity >= 60
                    ? "moderate to high humidity needing adequate ventilation and moisture management"
                    : annualAvgHumidity >= 40
                    ? "comfortable humidity levels with minimal moisture control requirements"
                    : "low humidity requiring humidification and moisture retention strategies"}{" "}
                  with an annual average of {annualAvgHumidity.toFixed(1)}%.
                </p>
                <p>
                  The {humidityRange.toFixed(1)}% annual humidity range{" "}
                  {humidityRange > 40
                    ? "indicates significant seasonal variation requiring adaptive moisture management"
                    : "shows relatively stable conditions with consistent humidity patterns"}{" "}
                  throughout the year, with {comfortablePercentage.toFixed(1)}%
                  of days in the comfortable range (40-60%).
                </p>
              </div>
            )}

            {/* Design Suggestion Text and AI Button */}
            <div className="flex items-center gap-3 mt-6">
              <span className="text-black text-sm font-bold">
                Design Suggestion
              </span>
              <button
                className="bg-gray-600 hover:bg-[#206ab2] text-white px-1.5 py-1 rounded text-xs font-bold transition-colors"
                onClick={async () => {
                  if (!OPENROUTER_API_KEY) {
                    setDesignError("OpenRouter API key not configured");
                    return;
                  }

                  setDesignSuggestions(null);
                  setDesignError(null);
                  setLoadingDesignSuggestions(true);

                  try {
                    const response = await fetch(
                      "https://openrouter.ai/api/v1/chat/completions",
                      {
                        method: "POST",
                        headers: {
                          Authorization: `Bearer ${OPENROUTER_API_KEY}`,
                          "HTTP-Referer": window.location.origin,
                          "X-Title": "ClimaPro.AI",
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          model: "anthropic/claude-3.7-sonnet",
                          route: "fallback",
                          messages: [
                            {
                              role: "system",
                              content:
                                "You are an expert building design consultant specializing in humidity management and moisture control in architecture. Analyze the humidity metrics provided to give location-specific building design recommendations. Structure your response as exactly 3 paragraphs: 1) Moisture control strategy, 2) Ventilation approach, 3) Material selection priorities. Include specific humidity numbers in your reasoning. Keep total response under 75 words with actionable, humidity-focused design advice.",
                            },
                            {
                              role: "user",
                              content: `For this ${climateInfo.mainClass}${
                                climateInfo.subClass
                              } (${
                                climateInfo.fullName
                              }) climate location, provide targeted building design recommendations based on these humidity metrics:

HUMIDITY PROFILE:
- Annual Average Humidity: ${annualAvgHumidity.toFixed(1)}%
- Maximum Humidity: ${maxHumidity.toFixed(1)}%
- Minimum Humidity: ${minHumidity.toFixed(1)}%
- Humidity Range: ${humidityRange.toFixed(1)}%
- Comfortable Days: ${comfortablePercentage.toFixed(1)}% (40-60% range)

Based on this humidity data, provide design recommendations with clear reasoning:

1. MOISTURE CONTROL: Explain vapor barrier and moisture management strategies based on the ${annualAvgHumidity.toFixed(
                                1
                              )}% average humidity. Reference these numbers in your reasoning.

2. VENTILATION APPROACH: Justify mechanical vs natural ventilation based on the ${humidityRange.toFixed(
                                1
                              )}% humidity range and seasonal patterns. Use these specific values in your explanation.

3. MATERIAL SELECTION: Recommend building materials and finishes based on the ${maxHumidity.toFixed(
                                1
                              )}% maximum humidity levels. Explain why these specific humidity numbers drive your recommendations.

Use the actual humidity values (${annualAvgHumidity.toFixed(
                                1
                              )}% avg, ${humidityRange.toFixed(
                                1
                              )}% range, ${comfortablePercentage.toFixed(
                                1
                              )}% comfort days) to justify each design decision.`,
                            },
                          ],
                        }),
                      }
                    );

                    if (!response.ok) {
                      if (response.status === 503) {
                        throw new Error("AI service temporarily unavailable");
                      } else if (response.status === 401) {
                        throw new Error("Authentication failed");
                      } else if (response.status === 429) {
                        throw new Error("Rate limit exceeded");
                      }
                      throw new Error(`Request failed: ${response.status}`);
                    }

                    const data = await response.json();

                    if (
                      data.choices &&
                      data.choices[0] &&
                      data.choices[0].message
                    ) {
                      setDesignSuggestions(data.choices[0].message.content);
                    } else if (data.error) {
                      throw new Error(data.error.message || "Unknown AI error");
                    }
                  } catch (error) {
                    console.error("AI humidity suggestions error:", error);
                    setDesignError(error.message);
                  } finally {
                    setLoadingDesignSuggestions(false);
                  }
                }}
                disabled={loadingDesignSuggestions}
              >
                {loadingDesignSuggestions ? "..." : "ClimaPro.AI"}
              </button>
            </div>
          </div>

          <div className="flex items-end justify-between mt-auto">
            <div>
              <div className="text-sm text-gray-600 mb-1">
                + MOISTURE CONTROL
              </div>
              <div className="text-sm text-gray-600">+ VENTILATION</div>
            </div>
            <div className="text-right">
              <div className="text-5xl font-bold text-gray-900">
                {comfortablePercentage.toFixed(1)}%
              </div>
              <div className="text-sm text-gray-500 font-bold">
                Comfortable Days
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Humidity Graph Card - Bottom Section */}
      <div className="bg-gray-100 border border-gray-500 rounded-[2rem] overflow-hidden">
        <button
          className="w-full p-4 flex justify-between items-center text-left hover:bg-gray-200 transition-colors"
          onClick={() => {
            const graphContent = document.getElementById(
              "humidity-graph-content"
            );
            const chevron = document.getElementById("humidity-graph-chevron");
            if (
              graphContent.style.display === "none" ||
              !graphContent.style.display
            ) {
              graphContent.style.display = "block";
              chevron.style.transform = "rotate(180deg)";
            } else {
              graphContent.style.display = "none";
              chevron.style.transform = "rotate(0deg)";
            }
          }}
        >
          <div className="flex items-center space-x-4 ml-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              GRAPH DATA
            </h3>
            <button
              className="bg-gray-600 hover:bg-[#206ab2] text-white px-1.5 py-1 rounded text-xs font-bold transition-colors"
              onClick={async (e) => {
                e.stopPropagation();

                if (!OPENROUTER_API_KEY) {
                  setAiError("OpenRouter API key not configured");
                  return;
                }

                // Clear previous results before starting new analysis
                setAiInterpretation(null);
                setAiError(null);
                setLoadingAiInterpretation(true);

                try {
                  // Calculate humidity statistics
                  const humidityStats = {
                    annualAvg: annualAvgHumidity.toFixed(1),
                    maxHumidity: maxHumidity.toFixed(1),
                    minHumidity: minHumidity.toFixed(1),
                    humidityRange: humidityRange.toFixed(1),
                    // Calculate seasonal averages
                    winterAvg: humidityData.data
                      ? (() => {
                          const winterData = humidityData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (humidityData.data.length / 12)
                                  ) + 1;
                              return month === 12 || month === 1 || month === 2;
                            }
                          );
                          return winterData.length > 0
                            ? (
                                winterData.reduce((sum, d) => sum + d.mean, 0) /
                                winterData.length
                              ).toFixed(1)
                            : "0";
                        })()
                      : "0",
                    springAvg: humidityData.data
                      ? (() => {
                          const springData = humidityData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (humidityData.data.length / 12)
                                  ) + 1;
                              return month === 3 || month === 4 || month === 5;
                            }
                          );
                          return springData.length > 0
                            ? (
                                springData.reduce((sum, d) => sum + d.mean, 0) /
                                springData.length
                              ).toFixed(1)
                            : "0";
                        })()
                      : "0",
                    summerAvg: humidityData.data
                      ? (() => {
                          const summerData = humidityData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (humidityData.data.length / 12)
                                  ) + 1;
                              return month === 6 || month === 7 || month === 8;
                            }
                          );
                          return summerData.length > 0
                            ? (
                                summerData.reduce((sum, d) => sum + d.mean, 0) /
                                summerData.length
                              ).toFixed(1)
                            : "0";
                        })()
                      : "0",
                    fallAvg: humidityData.data
                      ? (() => {
                          const fallData = humidityData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (humidityData.data.length / 12)
                                  ) + 1;
                              return (
                                month === 9 || month === 10 || month === 11
                              );
                            }
                          );
                          return fallData.length > 0
                            ? (
                                fallData.reduce((sum, d) => sum + d.mean, 0) /
                                fallData.length
                              ).toFixed(1)
                            : "0";
                        })()
                      : "0",
                  };

                  const response = await fetch(
                    "https://openrouter.ai/api/v1/chat/completions",
                    {
                      method: "POST",
                      headers: {
                        Authorization: `Bearer ${OPENROUTER_API_KEY}`,
                        "HTTP-Referer": window.location.origin,
                        "X-Title": "ClimaPro.AI",
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({
                        model: "anthropic/claude-3.7-sonnet",
                        route: "fallback",
                        messages: [
                          {
                            role: "system",
                            content:
                              "You are an expert climate data analyst specializing in humidity graph interpretation. Focus exclusively on analyzing humidity graph patterns, trends, seasonal variations, and ranges. Provide insights specifically about what the humidity graph reveals, not general climate advice. Keep responses concise, 100 words, focusing on graph-specific observations.",
                          },
                          {
                            role: "user",
                            content: `Analyze the humidity graph patterns for this ${
                              climateInfo.mainClass
                            }${climateInfo.subClass} (${
                              climateInfo.fullName
                            }) climate location:

ANNUAL STATISTICS:
- Annual Average: ${humidityStats.annualAvg}%
- Maximum Humidity: ${humidityStats.maxHumidity}%
- Minimum Humidity: ${humidityStats.minHumidity}%
- Total Annual Range: ${humidityStats.humidityRange}%

SEASONAL PATTERNS:
- Winter (Dec-Feb): ${humidityStats.winterAvg}% average
- Spring (Mar-May): ${humidityStats.springAvg}% average
- Summer (Jun-Aug): ${humidityStats.summerAvg}% average
- Fall (Sep-Nov): ${humidityStats.fallAvg}% average

SEASONAL HUMIDITY RANGES:
- Winter-Summer difference: ${(
                              parseFloat(humidityStats.summerAvg) -
                              parseFloat(humidityStats.winterAvg)
                            ).toFixed(1)}%
- Spring-Fall difference: ${(
                              parseFloat(humidityStats.springAvg) -
                              parseFloat(humidityStats.fallAvg)
                            ).toFixed(1)}%

Analyze specifically what this humidity graph reveals about:
1. Seasonal humidity transition patterns and smoothness
2. Humidity range and variability within this ${climateInfo.mainClass}${
                              climateInfo.subClass
                            } classification
3. Peak timing and duration patterns visible in the graph
4. Graph trend characteristics that define this climate type's moisture patterns
5. Notable humidity extremes or patterns shown in the data

Focus only on what the humidity graph data and patterns show, not design recommendations.`,
                          },
                        ],
                      }),
                    }
                  );

                  if (!response.ok) {
                    if (response.status === 503) {
                      throw new Error("AI service temporarily unavailable");
                    } else if (response.status === 401) {
                      throw new Error("Authentication failed");
                    } else if (response.status === 429) {
                      throw new Error("Rate limit exceeded");
                    }
                    throw new Error(`Request failed: ${response.status}`);
                  }

                  const data = await response.json();

                  if (
                    data.choices &&
                    data.choices[0] &&
                    data.choices[0].message
                  ) {
                    setAiInterpretation(data.choices[0].message.content);
                  } else if (data.error) {
                    throw new Error(data.error.message || "Unknown AI error");
                  }
                } catch (error) {
                  console.error("AI humidity interpretation error:", error);
                  setAiError(error.message);
                } finally {
                  setLoadingAiInterpretation(false);
                }
              }}
              disabled={loadingAiInterpretation}
            >
              {loadingAiInterpretation ? "..." : "ClimaPro.AI"}
            </button>
          </div>
          <svg
            id="humidity-graph-chevron"
            className="w-8 h-8 text-gray-600 transition-transform duration-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        <div
          id="humidity-graph-content"
          style={{ display: "none" }}
          className="px-8 pb-8"
        >
          {humidityData && humidityData.data && (
            <>
              {/* Conditional Layout: Split when AI content exists, full width otherwise */}
              {aiInterpretation || aiError || loadingAiInterpretation ? (
                <div className="flex gap-6">
                  {/* AI Interpretation Section - Left 30% */}
                  <div className="w-[30%] bg-gray-100">
                    <div className="h-full">
                      <h4 className="font-bold text-gray-800 mb-4">
                        AI Humidity Analysis
                      </h4>
                      {loadingAiInterpretation && (
                        <div className="space-y-3">
                          {/* Skeleton loading rectangles with color animation */}
                          <style jsx>{`
                            @keyframes humidityColorWave1 {
                              0%,
                              100% {
                                background-color: #d1d5db;
                              }
                              50% {
                                background-color: #9ca3af;
                              }
                            }
                            @keyframes humidityColorWave2 {
                              0%,
                              100% {
                                background-color: #9ca3af;
                              }
                              50% {
                                background-color: #d1d5db;
                              }
                            }
                            @keyframes humidityColorWave3 {
                              0%,
                              100% {
                                background-color: #e5e7eb;
                              }
                              50% {
                                background-color: #9ca3af;
                              }
                            }
                            .animate-humidity-wave-1 {
                              animation: humidityColorWave1 2s ease-in-out
                                infinite;
                            }
                            .animate-humidity-wave-2 {
                              animation: humidityColorWave2 2s ease-in-out
                                infinite 0.3s;
                            }
                            .animate-humidity-wave-3 {
                              animation: humidityColorWave3 2s ease-in-out
                                infinite 0.6s;
                            }
                          `}</style>
                          <div className="h-3 rounded animate-humidity-wave-1"></div>
                          <div className="h-3 rounded animate-humidity-wave-2 w-4/5"></div>
                          <div className="h-3 rounded animate-humidity-wave-3 w-3/5"></div>
                          <div className="h-3 rounded animate-humidity-wave-1 w-5/6"></div>
                          <div className="h-3 rounded animate-humidity-wave-2 w-2/3"></div>
                          <div className="h-3 rounded animate-humidity-wave-3"></div>
                          <div className="h-3 rounded animate-humidity-wave-1 w-3/4"></div>
                          <div className="h-3 rounded animate-humidity-wave-2 w-4/5"></div>
                          <div className="h-3 rounded animate-humidity-wave-3 w-1/2"></div>
                          <div className="h-3 rounded animate-humidity-wave-1 w-3/5"></div>
                          <div className="h-3 rounded animate-humidity-wave-2 w-5/6"></div>
                          <div className="h-3 rounded animate-humidity-wave-3 w-2/3"></div>
                        </div>
                      )}
                      {aiError && (
                        <div className="text-red-600 text-sm font-bold">
                          Error: {aiError}
                        </div>
                      )}
                      {aiInterpretation && (
                        <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap font-bold">
                          {aiInterpretation}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Graph Section - Right 70% when AI content exists */}
                  <div className="w-[70%] h-96 bg-gray-100 rounded-2xl p-6 flex flex-col justify-center">
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={humidityData.data.map((item, index) => ({
                            month: item.month
                              ? [
                                  "Jan",
                                  "Feb",
                                  "Mar",
                                  "Apr",
                                  "May",
                                  "Jun",
                                  "Jul",
                                  "Aug",
                                  "Sep",
                                  "Oct",
                                  "Nov",
                                  "Dec",
                                ][item.month - 1]
                              : [
                                  "Jan",
                                  "Feb",
                                  "Mar",
                                  "Apr",
                                  "May",
                                  "Jun",
                                  "Jul",
                                  "Aug",
                                  "Sep",
                                  "Oct",
                                  "Nov",
                                  "Dec",
                                ][
                                  Math.floor(
                                    index / (humidityData.data.length / 12)
                                  )
                                ],
                            humidity: item.mean,
                            dayOfYear: item.day_of_year || index,
                            originalIndex: index,
                          }))}
                          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#f0f0f0"
                          />
                          <XAxis
                            dataKey="month"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: "#666" }}
                            interval={
                              Math.floor(humidityData.data.length / 12) - 1
                            }
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: "#666" }}
                            label={{
                              value: "%",
                              angle: -90,
                              position: "insideLeft",
                            }}
                            domain={[0, 100]}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "white",
                              border: "1px solid #ccc",
                              borderRadius: "8px",
                            }}
                            formatter={(value) => [
                              `${value.toFixed(1)}%`,
                              "Humidity",
                            ]}
                          />

                          <ReferenceLine
                            y={40}
                            stroke="#e0e0e0"
                            strokeDasharray="2 2"
                          />
                          <ReferenceLine
                            y={60}
                            stroke="#e0e0e0"
                            strokeDasharray="2 2"
                          />

                          <Line
                            type="monotone"
                            dataKey="humidity"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            dot={false}
                            name="humidity"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="flex justify-center space-x-8 mt-4 text-sm">
                      <div className="flex items-center">
                        <div className="w-6 h-0.5 bg-blue-600 mr-3"></div>
                        <span className="text-gray-700">Relative Humidity</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-6 h-0.5 bg-gray-400 mr-3 border-dashed border-t"></div>
                        <span className="text-gray-700">
                          Comfort Range (40-60%)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Full Width Graph - When no AI content */
                <div className="h-96 bg-gray-100 rounded-2xl p-6 flex flex-col justify-center">
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={humidityData.data.map((item, index) => ({
                          month: item.month
                            ? [
                                "Jan",
                                "Feb",
                                "Mar",
                                "Apr",
                                "May",
                                "Jun",
                                "Jul",
                                "Aug",
                                "Sep",
                                "Oct",
                                "Nov",
                                "Dec",
                              ][item.month - 1]
                            : [
                                "Jan",
                                "Feb",
                                "Mar",
                                "Apr",
                                "May",
                                "Jun",
                                "Jul",
                                "Aug",
                                "Sep",
                                "Oct",
                                "Nov",
                                "Dec",
                              ][
                                Math.floor(
                                  index / (humidityData.data.length / 12)
                                )
                              ],
                          humidity: item.mean,
                          dayOfYear: item.day_of_year || index,
                          originalIndex: index,
                        }))}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="month"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: "#666" }}
                          interval={
                            Math.floor(humidityData.data.length / 12) - 1
                          }
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: "#666" }}
                          label={{
                            value: "%",
                            angle: -90,
                            position: "insideLeft",
                          }}
                          domain={[0, 100]}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "white",
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                          }}
                          formatter={(value) => [
                            `${value.toFixed(1)}%`,
                            "Humidity",
                          ]}
                        />

                        <ReferenceLine
                          y={40}
                          stroke="#e0e0e0"
                          strokeDasharray="2 2"
                        />
                        <ReferenceLine
                          y={60}
                          stroke="#e0e0e0"
                          strokeDasharray="2 2"
                        />

                        <Line
                          type="monotone"
                          dataKey="humidity"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          dot={false}
                          name="humidity"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="flex justify-center space-x-8 mt-4 text-sm">
                    <div className="flex items-center">
                      <div className="w-6 h-0.5 bg-blue-600 mr-3"></div>
                      <span className="text-gray-700">Relative Humidity</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-6 h-0.5 bg-gray-400 mr-3 border-dashed border-t"></div>
                      <span className="text-gray-700">
                        Comfort Range (40-60%)
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HumidityAnalysis;
